import type { CellChangeOrigin } from "@/utils/ixd-cell-model";

/**
 * IXD 셀 하단 한 줄 안내 — 셀 색과 모서리 삼각형이 무슨 뜻인지 적는다.
 *
 * 같은 설명을 tooltip 으로만 두면, 이 화면처럼 tooltip·클릭 패널이 많은 곳에서는
 * 열어 보기 전까지 숫자만 보인다. 워크북 셀이 반영불가 사유를 셀 안에 적는 것과
 * 같은 약속으로 IXD 셀에도 적는다.
 *
 * 알림(반영불가·단위배율)이 있는 셀은 비워 둔다 — 그 문구는 디자인에 아직 없고,
 * 지금도 표식과 패널이 사유 전문을 말한다.
 */
export const cellFootnote = ({
  hasNotice,
  linkedCount = 0,
  changeOrigin,
  hasValue,
  hadValue,
}: {
  hasNotice: boolean;
  /** 같은 identity 를 가진 다른 셀 수 — 값을 넣으면 이만큼 함께 바뀐다 */
  linkedCount?: number;
  /**
   * 지금 셀에 담긴 미저장 값이 어디서 왔는지. 값이 바뀐 셀만 문구를 갖는다 —
   * origin 이 늘어나면 그 문구를 여기서 정하라는 뜻이다.
   */
  changeOrigin?: CellChangeOrigin;
  /** 지금 셀에 보이는 값이 있는지 */
  hasValue: boolean;
  /** 반영 전 원래 값이 있었는지 — 값이 지워진 셀도 "바뀐 셀"이다 */
  hadValue: boolean;
}): string | undefined => {
  if (hasNotice) return undefined;
  // 문구는 "이 셀에 무슨 일이 있었나"를 말한다 — 값이 바뀌지 않은 셀에는 적지
  // 않는다. 연동 셀도 삼각형은 늘 떠 있지만 문구는 값이 바뀐 뒤에 붙는다.
  // 원래 값이 지워진 셀(워크북 쪽이 비어 빈 값이 반영됨)은 공란이어도 바뀐
  // 셀이므로 적는다. 원래도 비어 있던 셀만 비워 둔다 — 말할 변화가 없다.
  if (!changeOrigin || (!hasValue && !hadValue)) return undefined;
  // 연동이 먼저다. 값까지 들어간 연동 셀에서는 "몇 곳이 함께 바뀌었는지"가
  // "반영됐다"보다 많은 것을 말한다. 직접 수정으로 바뀐 연동 셀도 같이 바뀌므로
  // 같은 문구를 쓴다.
  if (linkedCount > 0) return `연동 계정 ${linkedCount}곳 함께 반영`;
  // 연동이 아닌 직접 수정(`manual`) 셀은 주황 표식이 이미 그 뜻이라 적지 않는다.
  if (changeOrigin === "value-apply") return "값 반영 완료";
  return undefined;
};
